import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { cn } from '../lib/utils'
import { Wrench, FileText, Receipt, DollarSign, ArrowRight, Clock3 } from 'lucide-react'
import { fetchJobs, fetchQuotes, fetchInvoices } from '../lib/api'
import { formatCurrency } from '@shopbay/shared'
import { ReadyToStartLabel, isJobReadyToStart, READY_BAR_CLASS, READY_TINT_CLASS, READY_ROW_CLASS } from '../components/jobs/ApprovedReadyBadge'
import { formatVehicleLabel } from '../lib/vehicleLabel'
import { getJobDisplayTitle } from '../lib/jobDisplayTitle'
import { useShopId } from '../hooks/useShopId'
import { usePageTitle } from '../hooks/usePageTitle'
import { useIsMobile } from '../hooks/useMediaQuery'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { StatusBadge } from '../components/ui/StatusBadge'
import { Skeleton } from '../components/ui/Skeleton'
import { EmptyState } from '../components/ui/EmptyState'
import { Button } from '../components/ui/Button'
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/Table'

export function Dashboard() {
  const shopId = useShopId()
  const isMobile = useIsMobile()
  usePageTitle('Dashboard')

  const { data: jobs, isLoading: jobsLoading } = useQuery({
    queryKey: ['jobs', shopId],
    queryFn: () => fetchJobs(shopId),
  })

  const { data: quotes, isLoading: quotesLoading } = useQuery({
    queryKey: ['quotes', shopId],
    queryFn: () => fetchQuotes(shopId),
  })

  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices', shopId],
    queryFn: () => fetchInvoices(shopId),
  })

  const recentJobs = jobs?.slice(0, 5) || []
  const openJobs = jobs?.filter((j: any) => j.status === 'open' || j.status === 'in_progress') || []
  const waitingParts = jobs?.filter((j: any) => j.status === 'waiting_parts') || []
  const pendingQuotes = quotes?.filter((q: any) => q.status === 'sent' || q.status === 'viewed') || []
  const unpaidInvoices = invoices?.filter((i: any) => i.status === 'sent' || i.status === 'overdue') || []
  const paidThisMonth = invoices?.filter((i: any) => {
    if (i.status !== 'paid') return false
    const paid = new Date(i.paidAt || i.createdAt)
    const now = new Date()
    return paid.getMonth() === now.getMonth() && paid.getFullYear() === now.getFullYear()
  }) || []

  const revenue = paidThisMonth.reduce((sum: number, i: any) => sum + parseFloat(i.total || '0'), 0)
  const unpaidBalance = unpaidInvoices.reduce((sum: number, i: any) => sum + parseFloat(i.total || '0'), 0)
  const avgTicket = paidThisMonth.length ? revenue / paidThisMonth.length : 0

  const loading = jobsLoading || quotesLoading || invoicesLoading

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-content-primary">Dashboard</h1>
        <div className="flex flex-wrap gap-2">
          <Button asChild size={isMobile ? 'sm' : 'md'}>
            <Link to="/jobs">
              Open Jobs
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="secondary" size={isMobile ? 'sm' : 'md'}>
            <Link to="/quotes">Review Quotes</Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        {[
          { label: 'Open Jobs', value: String(openJobs.length) },
          { label: 'Waiting Parts', value: String(waitingParts.length) },
          { label: 'Unpaid Balance', value: formatCurrency(unpaidBalance) },
          { label: 'Avg Ticket', value: formatCurrency(avgTicket) },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-4">
              <p className="text-[11px] uppercase tracking-wide text-content-tertiary">{stat.label}</p>
              {loading ? (
                <Skeleton className="mt-2 h-7 w-16" />
              ) : (
                <p className="mt-1 text-xl font-bold text-content-primary">{stat.value}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        {[
          { label: 'Open Jobs', value: openJobs.length, icon: Wrench, tone: 'text-info-500' },
          { label: 'Pending Quotes', value: pendingQuotes.length, icon: FileText, tone: 'text-warning-500' },
          { label: 'Unpaid Invoices', value: unpaidInvoices.length, icon: Receipt, tone: 'text-danger-500' },
          { label: 'Paid This Month', value: formatCurrency(revenue), icon: DollarSign, tone: 'text-success-500' },
        ].map((kpi) => (
          <Card key={kpi.label} className="bg-tint/5">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-content-tertiary">{kpi.label}</p>
                  {loading ? (
                    <Skeleton className="mt-2 h-7 w-16" />
                  ) : (
                    <p className="mt-1 text-xl font-bold text-content-primary">{kpi.value}</p>
                  )}
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-tint/5">
                  <kpi.icon className={`h-4 w-4 ${kpi.tone}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Current Queue</CardTitle>
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1 text-xs text-content-tertiary">
              <Clock3 className="h-3.5 w-3.5" />
              Last 5 jobs
            </div>
            <Button asChild variant="link" size="sm">
              <Link to="/jobs">View all</Link>
            </Button>
          </div>
        </CardHeader>
        {jobsLoading ? (
          <div className="space-y-3 p-4 md:p-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : recentJobs.length === 0 ? (
          <EmptyState
            icon={Wrench}
            title="No jobs yet"
            description="Create your first job to get started"
            action={{ label: 'Open Jobs', onClick: () => {} }}
          />
        ) : isMobile ? (
          <div className="divide-y divide-border">
            {recentJobs.map((job: any) => {
              const ready = isJobReadyToStart(job)
              return (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className={cn('relative flex items-center justify-between px-4 py-3 transition-colors', ready ? READY_TINT_CLASS : 'hover:bg-tint/5')}
              >
                {ready && <span aria-hidden="true" className={cn('absolute inset-y-0 left-0 w-[3px]', READY_BAR_CLASS)} />}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-content-primary truncate">{getJobDisplayTitle(job)}</p>
                  <p className="text-xs text-content-secondary truncate">
                    {formatVehicleLabel(job.vehicle)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  {ready ? <ReadyToStartLabel className="ml-3" /> : <StatusBadge entity="job" status={job.status} className="ml-3" />}
                </div>
              </Link>
              )
            })}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Job</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentJobs.map((job: any) => {
                const ready = isJobReadyToStart(job)
                return (
                <TableRow key={job.id} className={ready ? READY_ROW_CLASS : undefined}>
                  <TableCell>
                    <Link to={`/jobs/${job.id}`} className="font-medium text-content-link hover:underline">
                      {job.vehicle?.customer?.name || 'Unknown Customer'}
                    </Link>
                  </TableCell>
                  <TableCell className="text-content-secondary">
                    {formatVehicleLabel(job.vehicle)}
                  </TableCell>
                  <TableCell className="text-content-secondary">{getJobDisplayTitle(job)}</TableCell>
                  <TableCell>
                    {ready ? <ReadyToStartLabel /> : <StatusBadge entity="job" status={job.status} />}
                  </TableCell>
                </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  )
}
